const FALLBACK_TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
  'America/Sao_Paulo',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Moscow',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Asia/Hong_Kong',
  'Asia/Tokyo',
  'Asia/Seoul',
  'Australia/Sydney',
];

export const TIMEZONE_LIST: string[] = (() => {
  try {
    const supported = (Intl as any).supportedValuesOf?.('timeZone');
    if (Array.isArray(supported) && supported.length) return supported;
  } catch (e) {
    // fall through to fallback list
  }
  return FALLBACK_TIMEZONES;
})();

export const WORLD_CLOCK_CITIES: { city: string; timeZone: string }[] = [
  { city: 'New York', timeZone: 'America/New_York' },
  { city: 'London', timeZone: 'Europe/London' },
  { city: 'Dubai', timeZone: 'Asia/Dubai' },
  { city: 'Mumbai (IST)', timeZone: 'Asia/Kolkata' },
  { city: 'Singapore', timeZone: 'Asia/Singapore' },
];

export interface ZonedParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

export function zonedParts(date: Date, tz?: string): ZonedParts {
  const safeTz = tz || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: safeTz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(date);
  const get = (type: string) => parts.find(p => p.type === type)?.value || '0';
  let hour = Number(get('hour'));
  if (hour === 24) hour = 0;
  return {
    year: Number(get('year')),
    month: Number(get('month')),
    day: Number(get('day')),
    hour,
    minute: Number(get('minute')),
    second: Number(get('second')),
  };
}

function toUtcDate(date: Date): string {
  const yyyy = date.getUTCFullYear();
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(date.getUTCDate()).padStart(2, '0');
  const hh = String(date.getUTCHours()).padStart(2, '0');
  const min = String(date.getUTCMinutes()).padStart(2, '0');
  const ss = String(date.getUTCSeconds()).padStart(2, '0');
  return `${yyyy}/${mm}/${dd}, ${hh}:${min}:${ss}`;
}

export function zonedLocalToUtcIsoDate(localIso: string, tz?: string): string {
  if (!localIso) return '';
  const [datePart, timePart] = localIso.split('T');
  const [y, mo, d] = datePart.split('-').map(Number);
  const [h, mi] = (timePart || '00:00').split(':').map(Number);
  const targetMs = Date.UTC(y, mo - 1, d, h, mi);
  let guess = new Date(targetMs);
  for (let i = 0; i < 3; i++) {
    const wall = zonedParts(guess, tz);
    const wallMs = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute, wall.second);
    const diffMs = targetMs - wallMs;
    guess = new Date(guess.getTime() + diffMs);
  }
  const yyyy = guess.getUTCFullYear();
  const mm = String(guess.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(guess.getUTCDate()).padStart(2, '0');
  const hh = String(guess.getUTCHours()).padStart(2, '0');
  const min = String(guess.getUTCMinutes()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}T${hh}:${min}Z`;
}

/**
 * Interpret a "YYYY-MM-DDTHH:mm" wall-clock value as being in the given IANA
 * timezone and convert it to a UTC backend string "YYYY/MM/DD, HH:mm:ss".
 */
export function zonedLocalToUtcDate(localIso: string, tz?: string): string {
  if (!localIso) return '';
  const [datePart, timePart] = localIso.split('T');
  const [y, mo, d] = datePart.split('-').map(Number);
  const [h, mi] = (timePart || '00:00').split(':').map(Number);
  const targetMs = Date.UTC(y, mo - 1, d, h, mi);
  let guess = new Date(targetMs);
  for (let i = 0; i < 3; i++) {
    const wall = zonedParts(guess, tz);
    const wallMs = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute, wall.second);
    const diffMs = targetMs - wallMs;
    guess = new Date(guess.getTime() + diffMs);
  }
  return toUtcDate(guess);
}

/**
 * Convert a UTC backend string "YYYY/MM/DD, HH:mm:ss" into a local
 * "YYYY-MM-DDTHH:mm" wall-clock value in the given IANA timezone.
 */
export function utcDateToZonedLocal(utcStr: string, tz?: string): string {
  if (!utcStr) return '';
  let cleaned = utcStr.replace(/\//g, '-').replace(', ', 'T');
  if (!cleaned.endsWith('Z') && !cleaned.includes('+') && !cleaned.match(/-\d{2}:\d{2}$/)) {
    cleaned += 'Z';
  }
  const parsed = new Date(cleaned);
  if (isNaN(parsed.getTime())) return '';
  const parts = zonedParts(parsed, tz);
  const mm = String(parts.month).padStart(2, '0');
  const dd = String(parts.day).padStart(2, '0');
  const hh = String(parts.hour).padStart(2, '0');
  const min = String(parts.minute).padStart(2, '0');
  return `${parts.year}-${mm}-${dd}T${hh}:${min}`;
}

/** Render a UTC backend datetime string in a target timezone for display. */
export function formatUtcInTimezone(utcStr: string, tz?: string): string {
  if (!utcStr) return '';
  let cleaned = utcStr.replace(/\//g, '-').replace(', ', 'T');
  if (!cleaned.endsWith('Z') && !cleaned.includes('+') && !cleaned.match(/-\d{2}:\d{2}$/)) {
    cleaned += 'Z';
  }
  const parsed = new Date(cleaned);
  if (isNaN(parsed.getTime())) return utcStr;
  const parts = zonedParts(parsed, tz);
  const mm = String(parts.month).padStart(2, '0');
  const dd = String(parts.day).padStart(2, '0');
  let hh = parts.hour % 12;
  if (hh === 0) hh = 12;
  const ampm = parts.hour >= 12 ? 'PM' : 'AM';
  const min = String(parts.minute).padStart(2, '0');
  return `${parts.year}-${mm}-${dd} ${hh}:${min} ${ampm}`;
}
